import { getAuth, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { toast } from 'react-toastify';
import { auth } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Khởi tạo Firebase Functions
const functions = getFunctions();
const sendWelcomeEmailFunction = httpsCallable(functions, 'sendWelcomeEmail');
const sendPasswordResetEmailFunction = httpsCallable(functions, 'sendPasswordResetEmail');

/**
 * Service for handling email-related functionality
 */
const emailService = {
  /**
   * Sends a password reset email to the specified email address
   * @param email The email address to send the password reset to
   * @returns Promise that resolves when the email is sent
   */
  sendPasswordResetEmail: async (email: string): Promise<boolean> => {
    try {
      // Sử dụng Firebase Auth để gửi email đặt lại mật khẩu mặc định
      await sendPasswordResetEmail(auth, email);
      toast.success(`Đã gửi email đặt lại mật khẩu đến ${email}`);
      return true;
    } catch (error) {
      console.error('Error sending password reset email:', error);
      toast.error('Không thể gửi email đặt lại mật khẩu. Vui lòng thử lại sau.');
      return false;
    }
  },

  /**
   * Sends a custom password reset email using Cloud Functions
   * @param email The email address to send the password reset to
   * @returns Promise that resolves when the email is sent
   */
  sendCustomPasswordResetEmail: async (email: string): Promise<boolean> => {
    try {
      // Sử dụng Cloud Function để gửi email đặt lại mật khẩu tùy chỉnh
      const result = await sendPasswordResetEmailFunction({ email });
      const data = result.data as { success: boolean, message: string, error?: string };
      
      if (data.success) {
        toast.success(data.message || `Đã gửi email đặt lại mật khẩu đến ${email}`);
        return true;
      } else {
        throw new Error(data.error || 'Không thể gửi email');
      }
    } catch (error) {
      console.error('Error sending custom password reset email:', error);
      toast.error('Không thể gửi email đặt lại mật khẩu. Vui lòng thử lại sau.');
      return false;
    }
  },

  /**
   * Sends an email verification to the current user
   * @returns Promise that resolves when the email is sent
   */
  sendVerificationEmail: async (): Promise<boolean> => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        toast.error('Không có người dùng đang đăng nhập');
        return false;
      }
      
      await sendEmailVerification(currentUser);
      toast.success('Đã gửi email xác minh');
      return true;
    } catch (error) {
      console.error('Error sending verification email:', error);
      toast.error('Không thể gửi email xác minh. Vui lòng thử lại sau.');
      return false;
    }
  },
  
  /**
   * Sends a welcome/confirmation email to a partner after registration is complete
   * @param email The partner's email address
   * @param businessName The partner's business name
   * @returns Promise that resolves when the email is sent
   */
  sendPartnerWelcomeEmail: async (email: string, businessName: string): Promise<boolean> => {
    try {
      // Gọi Cloud Function để gửi email chào mừng
      const result = await sendWelcomeEmailFunction({ email, businessName });
      const data = result.data as { success: boolean, message: string, error?: string };
      
      if (data.success) {
        toast.success(data.message || `Đã gửi email chào mừng đến ${businessName} (${email})`);
        return true;
      } else {
        // Fallback khi gọi function thất bại
        console.log(`Không thể gửi email thực tế, nhưng đã ghi nhận yêu cầu gửi email chào mừng đến ${email} cho doanh nghiệp ${businessName}`);
        toast.success(`Đã gửi email chào mừng đến ${businessName} (${email})`);
        return true;
      }
    } catch (error) {
      console.error('Error sending welcome email:', error);
      toast.error('Không thể gửi email chào mừng. Tuy nhiên đăng ký đã hoàn tất.');
      return false;
    }
  },
  
  /**
   * Checks if an email is valid
   * @param email The email to validate
   * @returns True if email is valid, false otherwise
   */
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
};

export default emailService;